import type { RefObject } from 'react'
import { useEffect, useMemo, useState } from 'react'
import type { Position } from '../Tooltip'
import {
	getBottomStyles,
	getLeftStyles,
	getRightStyles,
	getTopStyles,
} from '../utils/tooltipStyles'
import { onTriggerChange, updateTooltipPosition } from '../utils/utils'

interface UseTooltipPositionProps {
	readonly show: boolean
	readonly position: Position
	readonly offset: number
	readonly reverse: boolean
	readonly content: string | React.ReactNode
	readonly tooltipRef: RefObject<HTMLDivElement | null>
	readonly triggerRef: RefObject<HTMLDivElement | null>
	readonly boundary?: { current?: HTMLElement | null }
	readonly minDistance: number
}

export const useTooltipPosition = ({
	show,
	position,
	offset,
	reverse,
	content,
	tooltipRef,
	triggerRef,
	boundary,
	minDistance,
}: UseTooltipPositionProps) => {
	const [limits, setLimits] = useState({
		left: minDistance,
		right:
			typeof document !== 'undefined'
				? document.body.clientWidth - minDistance
				: 0,
	})
	const [tooltipStyles, setTooltipStyles] = useState({})
	const [arrowStyles, setArrowStyles] = useState({})

	useEffect(() => {
		const handleTriggerChange = () => {
			onTriggerChange({
				boundary,
				setLimits,
				minDistance,
			})
		}

		handleTriggerChange()

		window.addEventListener('resize', handleTriggerChange)
		return () => window.removeEventListener('resize', handleTriggerChange)
	}, [boundary, show])

	const positions = useMemo(
		() => ({
			bottom: getBottomStyles,
			top: getTopStyles,
			left: getLeftStyles,
			right: getRightStyles,
		}),
		[content]
	)

	useEffect(() => {
		updateTooltipPosition({
			show,
			tooltipRef,
			triggerRef,
			position,
			offset,
			limits,
			positions,
			reverse,
			setArrowStyles,
			setTooltipStyles,
		})
	}, [limits, offset, position, positions, show, reverse])

	return {
		tooltipStyles,
		arrowStyles,
	}
}
